/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../index.css";
import {
  Box, Button, Menu, MenuItem, Dialog, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, Paper, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, TextField, Chip, Tooltip, Badge
} from "@mui/material";
import { MdMoreVert } from "react-icons/md";
import HomeIcon from '@mui/icons-material/Home';  // Home Icon
import { Grid, Loader2 } from "lucide-react";
import { ToastContainer, toast } from 'react-toastify';
import { IoMdAdd } from "react-icons/io";
import { UseAccessLevel } from "../AccessLevelContext";
import { IconButton } from "@mui/material";
import { useDebounce } from 'use-debounce';
import NotificationsIcon from '@mui/icons-material/Notifications';


// API
import { fetchSiteDetails, getPlantList, deleteSite, } from "../api/plant";
import { getUserDetails } from "../api/user";
import { fetchWeather, fetchLocation, getClientAlarms, get_sim_alerts, threshold_alarm_list } from "../api/others";

// Component
import colors from "../colour";
import { useDarkMode } from '../components/DarkModeContext';


//weather icons and bg
import clearsky_day from '../weatherimage/clearsky_day.png'
import cloudy_day from '../weatherimage/cloudy_day.png'
import raining_day from '../weatherimage/raining_day.png'
import thunderstorm_day from '../weatherimage/thunderstorm_day.png'
import clearsky_night from '../weatherimage/clearsky_night.png'
import cloudy_night from '../weatherimage/cloudy_night.png'
import raining_night from '../weatherimage/raining_day.png'
import thunderstorm_night from '../weatherimage/thunderstorm_day.png'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const PlantPage = () => {
  const navigate = useNavigate();
  const [customerSites, setCustomerSites] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [siteCount, setSiteCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { accessLevel } = UseAccessLevel();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState(""); //search plantlist
  const { darkMode } = useDarkMode(); // Access darkMode state
  const [weatherData, setWeatherData] = useState(null); //weather
  const [locationData, setLocationData] = useState(null);
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [countdown, setCountdown] = useState(5);
  const [clientId, setClientId] = useState(null);
  const [alarms, setAlarms] = useState([]);
  const [showAlarms, setShowAlarms] = useState(false);
  const alarmRef = useRef(null);
  const bellRef = useRef(null);
  const [simWarnings, setSimWarnings] = useState([]);
  const [activeTab, setActiveTab] = useState('alarms');
  const [thresholdAlarms, setThresholdAlarms] = useState([]);

  const fetchUserData = async () => {
    try {
      const data = await getPlantList()
      mapCustomerSites(data);
      await getSiteCount();

    } catch (error) {
      console.error("Error fetching user data:", error);
      navigate("/");
    } finally {
      setIsLoading(false);
    }
  };

  // Count Number of Site
  const getSiteCount = async () => {
    try {
      const plantList = await getPlantList(); // Call the getPlantList function
      const count = plantList.data.length; // Declare and initialize the count variable
      setSiteCount(count); // Set the site count
    } catch (error) {
      console.error('Error getting site count:', error);
      // Optionally, handle the error further or display a user-friendly message
    }
  };

  //fetch sim card alarm
  useEffect(() => {
    if (accessLevel !== 3) return;
    get_sim_alerts().then((alerts) => {
      setSimWarnings((alerts || []).map(a => ({
        iccid: a.iccid,
        label: a.label,
        remaining_mb: a.remaining_mb,
        isCritical: a.level === 'critical',  // ← use backend level
      })));
    });
  }, [accessLevel]);

  // Map Plant Details into proper data format
  const mapCustomerSites = useCallback((data) => {
    if (data?.data) {
      const sites = data.data.map((item) => ({
        image: item.image,
        id: item.client_id,
        name: item.company_name ? item.company_name.replace(/\s+/g, '-') : '', // Replace spaces with hyphens
        address: item.company_address,
        email: item.email,
        phone: item.phone_number,
        solarSysSize: item.solar_system_size,
        battSize: item.battery_capacity_size,
        battInv: item.battery_inverter_size,
        tariff: item.tariff,
        user_type: item.user_type,
        access_level: item.access_level,
        smartmeter_install_date: item.smartmeter_install_date,
        state: item.state,
        country: item.country,
        Solar: item.Solar,
        Grid: item.Grid,
        Others: item.Others

      }));
      setCustomerSites(sites);
    }
  }, []);


  // Handle Delete client
  const handleDeleteConfirm = async () => {
    try {
      await deleteSite(selectedSite.id);
      await fetchUserData();
      await getSiteCount()
      setIsDeleteDialogOpen(false);
      setSelectedSite(null);
      toast.success("Site deleted successfully!");
    } catch (error) {
      console.error("Error deleting site:", error);
      setIsDeleteDialogOpen(false);
      toast.error("Failed to delete site.");
    }
  };

  // Open Edit Form Popup
  const openEditPage = async (site) => {
    try {
      const selectedEditSite = await fetchSiteDetails(site.id);
      // Navigate to AddSite with user data as state
      navigate('/AddSite', {
        state: {
          editingUser: [site.id, selectedEditSite] // Pass ID and data
        }
      });
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };


  // Loading Element
  const LoadingOverlay = ({ type }) => {
    const message = type ? `Loading ${type} data...` : "Loading data...";

    return (
      <div className="flex flex-col items-center justify-center h-96 w-full">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
        <p className="text-lg text-gray-600">{message}</p>
      </div>
    );
  };

  // Pagination for the table
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredSites = useMemo(() => {
    // filter by debounced search
    let list = customerSites.filter(site =>
      site.name.toLowerCase().includes(debouncedSearch.toLowerCase())
      ||
      site.tariff.toLowerCase().includes(debouncedSearch.toLowerCase())
    );

    // if no sort key, just return
    if (!sortConfig.key) return list;

    // otherwise sort in place (make a copy first)
    return [...list].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // numeric compare
      if (!isNaN(aVal) && !isNaN(bVal)) {
        return sortConfig.direction === 'asc'
          ? parseFloat(aVal) - parseFloat(bVal)
          : parseFloat(bVal) - parseFloat(aVal);
      }
      // string compare
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [customerSites, debouncedSearch, sortConfig]);

  const handleSort = (field) => {
    let direction = 'asc';

    // Check if already sorted by this field, then toggle direction
    if (sortConfig.key === field && sortConfig.direction === 'asc') {
      direction = 'desc';  // If ascending, switch to descending
    }

    const sortedSites = [...customerSites].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];

      // If the value is numeric, compare numerically
      if (!isNaN(aValue) && !isNaN(bValue)) {
        const numA = parseFloat(aValue);
        const numB = parseFloat(bValue);
        return direction === 'asc' ? numA - numB : numB - numA;
      }

      // Otherwise, compare alphabetically for strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      }

      return 0;  // Equal values
    });

    setCustomerSites(sortedSites);
    setSortConfig({ key: field, direction }); // Store sorting config
  };

  // Weather Info
  const weatherCodes = {
    clearSky: [0],
    cloudySky: [1, 2, 3, 45, 48, 77],
    lightRaining: [51, 56, 57, 61, 66, 80],
    thunderstorm: [63, 65, 67, 81, 82, 53, 55, 95, 96, 99],
  };

  const getWeatherCategory = (code) => {
    if (weatherCodes.clearSky.includes(code)) return 'Clear Sky';
    if (weatherCodes.cloudySky.includes(code)) return 'Cloudy Sky';
    if (weatherCodes.lightRaining.includes(code)) return 'Light Raining';
    if (weatherCodes.thunderstorm.includes(code)) return 'Thunderstorm';
    return 'Unknown Weather';
  };

  const isDayTime = () => {
    const hour = new Date().getHours(); // Get current hour (0-23)
    return hour >= 6 && hour < 19; // True if between 6 AM - 6 PM
  };

  const getWeatherIcon = (weatherCode) => {
    const dayIcons = {
      'Clear Sky': clearsky_day,
      'Cloudy Sky': cloudy_day,
      'Light Raining': raining_day,
      'Thunderstorm': thunderstorm_day,
    };

    const nightIcons = {
      'Clear Sky': clearsky_night,
      'Cloudy Sky': cloudy_night,
      'Light Raining': raining_night,
      'Thunderstorm': thunderstorm_night,
    };

    return isDayTime()
      ? dayIcons[weatherCode] ?? clearsky_day
      : nightIcons[weatherCode] ?? clearsky_night;
  };

  const weatherDescription = getWeatherCategory(weatherData?.current_weather?.weathercode);
  const weatherIcon = getWeatherIcon(weatherDescription);

  useEffect(() => {
    fetchUserData();
    const fetchData = async () => {
      try {
        const data = await getUserDetails();
        const userId = data.client_id;
        setClientId(userId);
        // Ensure fetchWeatherAndLocation uses the latest user data

        await fetchWeatherAndLocation(data);

        const alarmsResponse = await getClientAlarms(userId);
        setAlarms(alarmsResponse?.data?.alarms || []);

      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchData(); // Call async function
  }, []);

  useEffect(() => {
    fetchUserData();
    const fetchData = async () => {
      try {
        const data = await getUserDetails();
        const userId = data.client_id;
        setClientId(userId);

        await fetchWeatherAndLocation(data);

        const alarmsResponse = await threshold_alarm_list(userId);
        setThresholdAlarms(alarmsResponse?.data || []);

      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchData();
  }, []);
  //weather forecast
  const fetchWeatherAndLocation = async (data) => {
    try {
      const lat = data["latitude"];
      const lon = data["longitude"];

      const [weather, location] = await Promise.all([
        fetchWeather(lat, lon),
        fetchLocation(lat, lon),
      ]);
      setWeatherData(weather);
      setLocationData(location.data);
    } catch (err) {
      console.error("Failed to fetch weather or location data", err);
    }
  };

  // Run countdown when dialog opens
  useEffect(() => {
    let timer;
    if (isDeleteDialogOpen) {
      setCountdown(5); // reset countdown when dialog opens
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer); // cleanup when dialog closes
  }, [isDeleteDialogOpen]);

  //User Type
  const statusColor = (user_type) => {
    switch (user_type) {
      case "eCOS":
        return "#6823c2ff";
      case "MRS":
        return "#0b0069ff";
      case "eCOS +":
        return "#2196f3";
      case "B-DES":
        return "rgb(203, 92, 255)";
      default:
        return "#9e9e9e";
    }
  };

  const userTypeMap = {
    0: "eCOS",
    1: "MRS",
    2: "eCOS +",
    6: "B-DES",
    null: " Inactive"
  };

  //Taruff Type
  const tariffstatusColor = (tariff) => {
    switch (tariff) {
      case "LVT":
        return "#090058ff";
      case "LVG":
        return "#6823c2ff";
      case "MVT":
        return "#0b0069b6";
      case "MVG":
        return "#6823c2b4";
      case "HVT":
        return "#000000ff";
      case "HVG":
        return "#3d0400ff";
      case "N6":
        return "#004d40ff";
      default:
        return "#9e9e9e";
    }
  };

  const tariffTypeMap = {
    "LVT": "LVT",
    "LVG": "LVG",
    "MVT": "MVT",
    "MVG": "MVG",
    "HVT": "HVT",
    "HVG": "HVG",
    "N6": "N6",
    null: " Inactive"
  };

  // Reset page to 0 whenever search changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  //testing the stauts dot
  const plantStatus = 1;

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If click is outside both the alarm panel AND the bell icon, close the panel
      if (
        alarmRef.current &&
        !alarmRef.current.contains(event.target) &&
        bellRef.current &&
        !bellRef.current.contains(event.target)
      ) {
        setShowAlarms(false);
      }
    };

    // Add event listener when panel is open
    if (showAlarms) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAlarms]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: 'column',

      }}>
      <ToastContainer />
      <Box
        className={darkMode ? "header_darkM" : "header_lightM"}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 2,
          borderBottom: `1px solid ${darkMode ? colors.d3 : colors.l}`,
          backgroundColor: darkMode ? colors.d1 : colors.l1,
          width: '100%',
          alignItems: "center", // Center vertically
        }}>
        <Box sx={{
          width: { xs: '50%', md: '70%' },
          display: "flex",
          justifyContent: "flex-start", // Center horizontally
          alignItems: "center", // Center vertically
          height: "100%", // Ensure height for vertical centering
          marginLeft: '2rem'
        }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "Montserrat",
              fontWeight: "bold",
              fontSize: "2vw",
              color: darkMode ? colors.f2 : colors.f3,
            }}>
            Management Reporting System
          </Typography>
        </Box>
        <Box sx={{
          width: { xs: '20%', md: '30%' },
          display: "flex",
          justifyContent: "flex-end", // Center horizontally
          alignItems: "center", // Center vertically
          height: "100%", // Ensure height for vertical centering
        }}>

        </Box>
      </Box>
      {/* ----------------Section 1--------------------- */}
      <Box
        sx={{ display: "flex", justifyContent: "center", gap: { xs: 3, md: 2 }, p: 2, width: "98%", margin: "auto" }}>
        <IconButton
          ref={bellRef}
          onClick={() => setShowAlarms(true)}
          sx={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 999,
            backgroundColor: darkMode ? colors.d1 : colors.l1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: darkMode ? colors.d2 : colors.l2,
            }
          }}
        >
          <Badge
            badgeContent={alarms.length + (accessLevel === 3 ? simWarnings.length : 0)}
            color={
              alarms.some(a => a.status === 2) || (accessLevel === 3 && simWarnings.some(s => s.isCritical))
                ? "error"
                : "warning"
            }
            max={99}
          >
            <NotificationsIcon sx={{ color: darkMode ? '#fff' : '#333', fontSize: 30 }} />
          </Badge>
        </IconButton>

        {/* Alarm Popup */}
        {showAlarms && (
          <Box
            ref={alarmRef}
            sx={{
              position: 'fixed',
              top: '80px',
              right: '20px',
              zIndex: 1000,
              width: '400px',
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto',
              background: darkMode ? colors.d2 : colors.l2,
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              padding: '1.5rem',
              animation: 'slideIn 0.3s ease',
              '@keyframes slideIn': {
                from: { opacity: 0, transform: 'translateY(-20px)' },
                to: { opacity: 1, transform: 'translateY(0)' }
              }
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: darkMode ? '#fff' : '#000', fontWeight: 600 }}>
                🔔 Alarms & Notifications
              </Typography>
            </Box>

            {/* Tabs - always visible, SIM tab only shows for accessLevel === 3 */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {/* Meter Alarms Tab - visible to all users */}
              <Box
                onClick={() => setActiveTab('alarms')}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 0.8,
                  px: 0.5,
                  borderRadius: 2,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 13,
                  minHeight: 44,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.3,
                  bgcolor: activeTab === 'alarms' ? '#d32f2f' : (darkMode ? colors.d1 : colors.l1),
                  color: activeTab === 'alarms' ? '#fff' : (darkMode ? '#fff' : '#333'),
                  transition: 'all 0.2s',
                }}
              >
                <span>⚡ Meter Alarms</span>
                {accessLevel === 3 && alarms.length > 0 && (
                  <span style={{
                    background: activeTab === 'alarms' ? 'rgba(255,255,255,0.3)' : '#d32f2f',
                    color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11
                  }}>
                    {alarms.length}
                  </span>
                )}
              </Box>

              {/* SIM Cards Tab - admin only */}
              {(accessLevel === 3) && (
                <Box
                  onClick={() => setActiveTab('sim')}
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    py: 0.8,
                    px: 0.5,
                    borderRadius: 2,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                    minHeight: 44,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.3,
                    bgcolor: activeTab === 'sim' ? '#7c3aed' : (darkMode ? colors.d1 : colors.l1),
                    color: activeTab === 'sim' ? '#fff' : (darkMode ? '#fff' : '#333'),
                    transition: 'all 0.2s',
                  }}
                >
                  <span>📡 SIM Cards</span>
                  {simWarnings.length > 0 && (
                    <span style={{
                      background: activeTab === 'sim' ? 'rgba(255,255,255,0.3)' : '#7c3aed',
                      color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11
                    }}>
                      {simWarnings.length}
                    </span>
                  )}
                </Box>
              )}

              {/* Threshold Alarms Tab - visible to all users */}
              <Box
                onClick={() => setActiveTab('threshold')}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 0.8,
                  px: 0.5,
                  borderRadius: 2,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 13,
                  minHeight: 44,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.3,
                  bgcolor: activeTab === 'threshold' ? '#ed6c02' : (darkMode ? colors.d1 : colors.l1),
                  color: activeTab === 'threshold' ? '#fff' : (darkMode ? '#fff' : '#333'),
                  transition: 'all 0.2s',
                }}
              >
                <span>📊 Threshold</span>
                {accessLevel === 3 && thresholdAlarms.length > 0 && (
                  <span style={{
                    background: activeTab === 'threshold' ? 'rgba(255,255,255,0.3)' : '#ed6c02',
                    color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11
                  }}>
                    {thresholdAlarms.length}
                  </span>
                )}
              </Box>
            </Box>
            {/* ── METER ALARMS TAB (all users) ── */}
            {activeTab === 'alarms' && (
              <Box>
                {/* Stats */}
                <Box sx={{ display: "flex", gap: "10px", mb: 2 }}>
                  <Paper sx={{ flex: 1, p: 1, textAlign: "center", background: darkMode ? colors.d1 : colors.l1 }}>
                    <Typography variant="h6">{alarms.length}</Typography>
                    <Typography variant="caption">Total</Typography>
                  </Paper>
                  <Paper sx={{ flex: 1, p: 1, textAlign: "center", background: '#ffebee' }}>
                    <Typography variant="h6" sx={{ color: '#d32f2f' }}>
                      {alarms.filter(a => a.status === 2).length}
                    </Typography>
                    <Typography variant="caption">Critical</Typography>
                  </Paper>
                  <Paper sx={{ flex: 1, p: 1, textAlign: "center", background: '#fff3e0' }}>
                    <Typography variant="h6" sx={{ color: '#ed6c02' }}>
                      {alarms.filter(a => a.status === 1).length}
                    </Typography>
                    <Typography variant="caption">Warning</Typography>
                  </Paper>
                </Box>

                {/* Alarms List */}
                {alarms.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: "center", background: darkMode ? colors.d1 : colors.l1 }}>
                    <Typography variant="h3" sx={{ fontSize: "2rem", mb: 1 }}>✅</Typography>
                    <Typography>No active alarms</Typography>
                  </Paper>
                ) : (
                  alarms.map((alarm) => (
                    <Paper key={alarm.alarm_id} sx={{
                      p: 1.5, mb: 1,
                      background: darkMode ? colors.d1 : colors.l1,
                      borderLeft: `4px solid ${alarm.status === 2 ? '#d32f2f' : '#ed6c02'}`,
                    }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="caption" sx={{
                          background: alarm.status === 2 ? '#ffebee' : '#fff3e0',
                          color: alarm.status === 2 ? '#d32f2f' : '#ed6c02',
                          px: 1, py: 0.5, borderRadius: 1, fontWeight: 600
                        }}>
                          {alarm.status === 2 ? '🔴 CRITICAL' : '🟡 WARNING'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {alarm.company_name} ({alarm.serial_number})
                      </Typography>
                      <Typography variant="caption" sx={{ color: darkMode ? '#aaa' : '#666', display: 'block' }}>
                        📅 From {new Date(alarm.start_time).toLocaleString()} until {alarm.end_time ? new Date(alarm.end_time).toLocaleString() : "now"}
                      </Typography>
                    </Paper>
                  ))
                )}
              </Box>
            )}

            {/* ── SIM CARDS TAB (accessLevel === 3 only) ── */}
            {activeTab === 'sim' && (accessLevel === 3) && (
              <Box>
                {/* Stats */}
                <Box sx={{ display: "flex", gap: "10px", mb: 2 }}>
                  <Paper sx={{ flex: 1, p: 1, textAlign: "center", background: darkMode ? colors.d1 : colors.l1 }}>
                    <Typography variant="h6">{simWarnings.length}</Typography>
                    <Typography variant="caption">Total Alerts</Typography>
                  </Paper>
                  <Paper sx={{ flex: 1, p: 1, textAlign: "center", background: '#ffebee' }}>
                    <Typography variant="h6" sx={{ color: '#d32f2f' }}>
                      {simWarnings.filter(s => s.isCritical).length}
                    </Typography>
                    <Typography variant="caption">Critical &lt;100MB</Typography>
                  </Paper>
                  <Paper sx={{ flex: 1, p: 1, textAlign: "center", background: '#fff3e0' }}>
                    <Typography variant="h6" sx={{ color: '#ed6c02' }}>
                      {simWarnings.filter(s => !s.isCritical).length}
                    </Typography>
                    <Typography variant="caption">Warning &lt;150MB</Typography>
                  </Paper>
                </Box>

                {/* SIM Alerts List */}
                {simWarnings.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: "center", background: darkMode ? colors.d1 : colors.l1 }}>
                    <Typography variant="h3" sx={{ fontSize: "2rem", mb: 1 }}>✅</Typography>
                    <Typography>All SIM cards are healthy</Typography>
                  </Paper>
                ) : (
                  simWarnings.map((sim) => (
                    <Paper key={sim.iccid} sx={{
                      p: 1.5, mb: 1,
                      background: darkMode ? colors.d1 : colors.l1,
                      borderLeft: `4px solid ${sim.isCritical ? '#d32f2f' : '#ed6c02'}`,
                    }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="caption" sx={{
                          background: sim.isCritical ? '#ffebee' : '#fff3e0',
                          color: sim.isCritical ? '#d32f2f' : '#ed6c02',
                          px: 1, py: 0.5, borderRadius: 1, fontWeight: 600
                        }}>
                          {sim.isCritical ? '🔴 CRITICAL' : '🟡 WARNING'}
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: sim.isCritical ? '#d32f2f' : '#ed6c02',
                          fontWeight: 700
                        }}>
                          {sim.remaining_mb.toFixed(2)} MB left
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {sim.label || sim.iccid}
                      </Typography>
                      <Typography variant="caption" sx={{ color: darkMode ? '#aaa' : '#666', display: 'block' }}>
                        📡 {sim.iccid}
                      </Typography>
                    </Paper>
                  ))
                )}
              </Box>
            )}

            {/* ── THRESHOLD ALARMS TAB (all users) ── */}
            {activeTab === 'threshold' && (
              <Box>
                {/* Stats */}
                <Box sx={{ display: "flex", gap: "10px", mb: 2 }}>
                  <Paper sx={{ flex: 1, p: 1, textAlign: "center", background: darkMode ? colors.d1 : colors.l1 }}>
                    <Typography variant="h6">{thresholdAlarms.length}</Typography>
                    <Typography variant="caption">Total</Typography>
                  </Paper>
                  <Paper sx={{ flex: 1, p: 1, textAlign: "center", background: '#ffebee' }}>
                    <Typography variant="h6" sx={{ color: '#d32f2f' }}>
                      {thresholdAlarms.filter(a => a.level_name === 'critical').length}
                    </Typography>
                    <Typography variant="caption">Critical</Typography>
                  </Paper>
                  <Paper sx={{ flex: 1, p: 1, textAlign: "center", background: '#fff3e0' }}>
                    <Typography variant="h6" sx={{ color: '#ed6c02' }}>
                      {thresholdAlarms.filter(a => a.level_name === 'warning').length}
                    </Typography>
                    <Typography variant="caption">Warning</Typography>
                  </Paper>
                </Box>

                {/* Threshold Alarms List */}
                {thresholdAlarms.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: "center", background: darkMode ? colors.d1 : colors.l1 }}>
                    <Typography variant="h3" sx={{ fontSize: "2rem", mb: 1 }}>✅</Typography>
                    <Typography>No threshold alarms</Typography>
                  </Paper>
                ) : (
                  thresholdAlarms.map((alarm, index) => (
                    <Paper
                      key={`${alarm.serial_number}-${alarm.triggered_at}-${index}`}
                      sx={{
                        p: 1.5, mb: 1,
                        background: darkMode ? colors.d1 : colors.l1,
                        borderLeft: `4px solid ${alarm.level_name === 'critical' ? '#d32f2f' : '#ed6c02'}`,
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="caption" sx={{
                          background: alarm.level_name === 'critical' ? '#ffebee' : '#fff3e0',
                          color: alarm.level_name === 'critical' ? '#d32f2f' : '#ed6c02',
                          px: 1, py: 0.5, borderRadius: 1, fontWeight: 600
                        }}>
                          {alarm.level_name === 'critical' ? '🔴 CRITICAL' : '🟡 WARNING'}
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: alarm.level_name === 'critical' ? '#d32f2f' : '#ed6c02',
                          fontWeight: 700
                        }}>
                          {alarm.kw_value} kW
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {alarm.description} ({alarm.serial_number})
                      </Typography>
                      <Typography variant="caption" sx={{ color: darkMode ? '#aaa' : '#666', display: 'block' }}>
                        📅 {new Date(alarm.triggered_at).toLocaleString()}
                      </Typography>
                    </Paper>
                  ))
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Total Plant Box */}
        <Box
          sx={{
            backgroundImage: `linear-gradient(to right, #100c3f,rgb(81, 79, 112),  #100c3f)`,
            backgroundSize: "140%",
            backgroundPosition: "center",
            width: "40%",
            height: "40vh",
            padding: { xs: '1rem', md: '2rem' },
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            m: "1% 0",
            border: '1px solid',
            borderColor: darkMode ? colors.d3 : colors.l3,
          }}>
          <HomeIcon sx={{ fontSize: "4vw", color: darkMode ? colors.d4 : colors.f2, mb: 1 }} />
          <Typography sx={{ fontSize: "2vw", fontWeight: "bold", mb: 1, color: colors.f2 }}>
            Total Plant
          </Typography>
          <Typography sx={{ fontSize: "3vw", fontWeight: 700, fontFamily: 'Times New Roman, serif', color: colors.f2 }}>
            {siteCount !== null ? siteCount : 'Loading...'}
          </Typography>
        </Box>

        {/* Current Weather */}
        <Box
          sx={{
            backgroundImage: `linear-gradient(to right, #100c3f,rgb(81, 79, 112),  #100c3f)`,
            backgroundSize: "100%",
            backgroundPosition: "center-top",
            width: "60%",
            height: "40vh",
            padding: { xs: '1rem', md: '2rem' },
            borderRadius: "20px",
            display: "grid",
            flexDirection: "column",
            border: '1px solid',
            borderColor: darkMode ? colors.d2 : colors.l3,
            textAlign: "center",
            m: "1% 0",
            alignContent: "center",
          }}>
          {weatherData && locationData ? (<>
            <Box sx={{ gridArea: "1 / 1 / 2 / 2", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <img src={weatherIcon} alt={weatherDescription} style={{ width: "10vw", height: "10vw" }} />
            </Box>
            <Box sx={{ gridArea: "2 / 1 / 3 / 2", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Typography sx={{
                fontSize: '1.4vw', fontWeight: 'bold', color: colors.f2,
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)', letterSpacing: '0.5px', marginBottom: 1
              }}>
                {weatherDescription}
              </Typography>
            </Box>
            <Box sx={{ gridArea: "1 / 2 / 3 / 4", borderRadius: 5, padding: 2 }}>
              <Typography sx={{ fontSize: '1.8vw', color: colors.f2, fontWeight: 'bold', marginBottom: 1 }}>
                {locationData?.city ?? "Unknown City"}, {locationData?.country ?? "Unknown Country"}
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1, borderRadius: 2, padding: 2 }}>
                  <Typography sx={{ fontSize: '1.3vw', color: colors.f2, fontWeight: 'bold' }}>
                    🌡️ {weatherData.current_weather.temperature}°C
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, borderRadius: 2, padding: 2 }}>
                  <Typography sx={{ fontSize: " 1.3vw", color: colors.f2, fontWeight: 'bold' }}>
                    ☀️ {weatherData.hourly.shortwave_radiation[new Date().getHours()]} W/m²
                  </Typography>
                </Box>
              </Box>
              {Array.isArray(weatherData?.hourly?.temperature_2m) && (
                <Box sx={{ marginBottom: "2vh", position: 'relative', height: '0.8vh', backgroundColor: colors.f1, borderRadius: 3 }}>
                  <Box sx={{
                    position: 'absolute', height: '100%', borderRadius: 3, left: 0, transition: 'width 0.5s ease',
                    backgroundColor: colors.f2,
                    width: `${((weatherData.current_weather.temperature - Math.min(...weatherData.hourly.temperature_2m)) / (Math.max(...weatherData.hourly.temperature_2m) - Math.min(...weatherData.hourly.temperature_2m))) * 100}%`,
                  }} />
                  <Box sx={{
                    position: 'absolute', top: '-0.4vh', width: '0.7vw', height: '0.7vw',
                    backgroundColor: colors.d4, borderRadius: '50%', transform: 'translateX(-50%)',
                    left: `${((weatherData.current_weather.temperature - Math.min(...weatherData.hourly.temperature_2m)) / (Math.max(...weatherData.hourly.temperature_2m) - Math.min(...weatherData.hourly.temperature_2m))) * 100}%`,
                  }} />
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography sx={{ fontSize: '0.9vw', color: colors.f2, fontWeight: '500' }}>
                  {Math.min(...weatherData.hourly.temperature_2m)}°C
                </Typography>
                <Typography sx={{ fontSize: '0.9vw', color: colors.f2, fontWeight: '500' }}>
                  {Math.max(...weatherData.hourly.temperature_2m)}°C
                </Typography>
              </Box>
              {Array.isArray(weatherData?.hourly?.temperature_2m) && (
                <Box sx={{ marginBottom: "2vh", position: 'relative', height: '0.8vh', backgroundColor: colors.f1, borderRadius: 3 }}>
                  <Box sx={{
                    position: 'absolute', height: '100%', borderRadius: 3, left: 0, transition: 'width 0.5s ease',
                    backgroundColor: colors.f2,
                    width: `${((weatherData.hourly.shortwave_radiation[new Date().getHours()] - Math.min(...weatherData.hourly.shortwave_radiation)) / (Math.max(...weatherData.hourly.shortwave_radiation) - Math.min(...weatherData.hourly.shortwave_radiation))) * 100}%`,
                  }} />
                  <Box sx={{
                    position: 'absolute', top: '-0.4vh', width: '0.7vw', height: '0.7vw',
                    backgroundColor: colors.d4, borderRadius: '50%', transform: 'translateX(-50%)',
                    left: `${((weatherData.hourly.shortwave_radiation[new Date().getHours()] - Math.min(...weatherData.hourly.shortwave_radiation)) / (Math.max(...weatherData.hourly.shortwave_radiation) - Math.min(...weatherData.hourly.shortwave_radiation))) * 100}%`,
                  }} />
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography sx={{ fontSize: '0.9vw', color: colors.f2, fontWeight: '500' }}>
                  {Math.min(...weatherData.hourly.shortwave_radiation)}W/m²
                </Typography>
                <Typography sx={{ fontSize: '0.9vw', color: colors.f2, fontWeight: '500' }}>
                  {Math.max(...weatherData.hourly.shortwave_radiation)}W/m²
                </Typography>
              </Box>
            </Box>
          </>) : (
            <Typography sx={{ fontSize: "2vw", fontWeight: 700, fontFamily: 'Times New Roman, serif', justifyContent: 'center', alignItems: 'center', color: colors.f2 }}>
              Loading weather data...
            </Typography>
          )}
        </Box>
      </Box>

      {/* Plant List Table */}
      <Box
        sx={{
          background: darkMode ? colors.d2 : colors.l2,
          width: "97%",
          height: "auto",
          display: "flex",
          margin: "auto",
          padding: "2rem",
          borderRadius: "20px",
          flexDirection: "column",
        }}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "flex-start", // Center horizontally
          alignItems: "center", // Center vertically
          height: "5vh", // Ensure height for vertical centering
          margin: '1rem'
        }}>
          <Typography
            variant="h1"
            sx={{
              fontFamily: "Montserrat",
              fontWeight: "bold",
              fontSize: "2vw",
              color: darkMode ? colors.f2 : colors.f3,
            }}>
            Plant List
          </Typography>
        </Box>
        {isLoading ? (
          <LoadingOverlay />
        ) : (
          <Paper sx={{ width: '100%', maxHeight: 'auto', borderRadius: '15px', overflow: 'auto', border: '1px solid', borderColor: darkMode ? "" : colors.l3 }}>
            <TableContainer sx={{ backgroundColor: colors.b1, mt: 0, width: "100%", maxWidth: "100%" }}>
              <Table sx={{ tableLayout: "auto", width: "100%" }}>

                {/* Sticky Table Head */}
                <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: darkMode ? colors.d3 : colors.l4 }}>

                  {/* Search and Add Site Section */}
                  <TableRow>
                    <TableCell colSpan={accessLevel === 3 ? 8 : 7} sx={{ backgroundColor: colors.b1, borderBottom: `1px solid ${darkMode ? colors.d3 : colors.l3}`, padding: 0, width: "100%" }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px',
                          width: '100%',
                          backgroundColor: darkMode ? colors.d3 : colors.l2,
                          position: 'sticky',
                          top: 0,
                          zIndex: 1100,
                        }}
                      >
                        {/* Search Box */}
                        <Box sx={{ flex: 1, maxWidth: '70%' }}>
                          <TextField
                            label="Search (Company Name or Tariff)"
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                              width: '100%',
                              '& label': { color: darkMode ? colors.f2 : colors.l4 },
                              '& label.Mui-focused': { color: colors.f1 },
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: darkMode ? colors.f2 : colors.l4 },
                                '&:hover fieldset': { borderColor: darkMode ? colors.d4 : colors.l4 },
                                '&.Mui-focused fieldset': { borderColor: colors.f1 },
                              },
                              '& .MuiInputBase-input': {
                                color: darkMode ? colors.f2 : colors.f3,
                              },
                            }}
                          />
                        </Box>

                        {/* Add Site Button */}
                        <Box sx={{ flex: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                          {(accessLevel === 3) && (
                            <Button
                              variant="contained"
                              startIcon={<IoMdAdd style={{ fontSize: 'inherit' }} />}
                              onClick={() => navigate('/AddSite')}
                              sx={{
                                fontSize: { xs: '9px', md: '14px' },
                                backgroundColor: darkMode ? colors.d2 : colors.l4,
                                color: colors.f2,
                                padding: '8px 16px',
                                minWidth: '120px',
                                borderRadius: '8px',
                                '&:hover': {
                                  backgroundColor: darkMode ? colors.d4 : colors.l5,
                                },
                              }}
                            >
                              Add Site
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Column Headers */}
                  <TableRow sx={{ position: 'sticky', top: 48, zIndex: 1000, backgroundColor: darkMode ? colors.d3 : colors.l4 }}>
                    <TableCell sx={{ fontSize: { xs: "10px", md: "14px" }, color: colors.f2, fontWeight: "bold", width: "15%", }}>
                      Company Name
                      <IconButton onClick={() => handleSort('name')}>
                        {sortConfig.direction === 'asc' ? (
                          <ArrowDropDownIcon sx={{ color: "white" }} />
                        ) : (
                          <ArrowDropUpIcon sx={{ color: "white" }} />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: "10px", md: "14px" }, textAlign: "center", color: colors.f2, fontWeight: "bold", width: "7%" }}>
                      Created At
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: "10px", md: "14px" }, color: colors.f2, fontWeight: "bold", width: "7%", textAlign: "center" }}>
                      Tariff
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: "10px", md: "14px" }, color: colors.f2, fontWeight: "bold", width: "7%", textAlign: "center" }}>
                      Type
                    </TableCell>
                    {/* <TableCell sx={{ fontSize: { xs: "10px", md: "14px" }, color: colors.f2, fontWeight: "bold", width: "7%", textAlign: "center" }}>
                      Status
                    </TableCell> */}
                    <TableCell sx={{ fontSize: { xs: "10px", md: "14px" }, color: colors.f2, fontWeight: "bold", width: "8%", textAlign: "center" }}>
                      Access Level
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: "10px", md: "14px" }, color: colors.f2, fontWeight: "bold", width: "43%", textAlign: "center" }}>

                    </TableCell>
                    {(accessLevel === 3) && (
                      <TableCell align="center" sx={{ fontSize: { xs: "10px", md: "14px" }, color: colors.f2, fontWeight: "bold", width: "6%" }}>
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>

                {/* Table Body */}
                <TableBody>
                  {filteredSites.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((site) => (
                    <TableRow
                      key={site.id}
                      sx={{
                        backgroundColor: hoveredRow === site.id ? colors.h1 : colors.f2,
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        if (!e.target.closest("td[data-action]")) {

                          if (site.user_type === 6) {
                            navigate("/BESS_Designer", { state: { siteId: site.id } });
                          } else {
                            navigate(`/dashboard/${site.name}`, { state: { siteId: site.id } });
                          }

                        }
                      }}
                      onMouseEnter={() => setHoveredRow(site.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <TableCell sx={{ color: "#000000" }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {site.name.replace(/-/g, " ")}

                          <Tooltip
                            sx={{ width: "200px" }}
                            arrow
                            title={
                              <Box>
                                {[
                                  { label: "Address", value: site.address },
                                  { label: "Solar Size", value: `${site.solarSysSize || 0} kWp` },
                                  { label: "Battery Capacity", value: `${site.battSize || 0} kWh` },
                                  { label: "Battery Inverter Size", value: `${site.battInv || 0} kWac` },
                                  { label: "Solar Smart Meter", value: site.Solar || "Not Installed" },
                                  { label: "Grid Smart Meter", value: site.Grid || "Not Installed" },
                                  { label: "Other Smart Meter", value: site.Others || "Not Installed" },
                                ].map((item, index) => (
                                  <Typography key={index}>
                                    <Typography component="span" fontWeight="bold">
                                      {item.label}:
                                    </Typography>{" "}
                                    {item.value}
                                  </Typography>
                                ))}
                              </Box>
                            }
                          >
                            <HelpOutlineIcon
                              sx={{
                                fontSize: '16px',
                                opacity: 0.7,
                                cursor: 'pointer',
                                '&:hover': { opacity: 1 }
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: "10px", md: "14px" }, textAlign: "center" }}>  {site.smartmeter_install_date ?? "2025-1-1"}</TableCell>
                      <TableCell sx={{ fontSize: { xs: "10px", md: "14px", textAlign: "center" } }}>
                        <Chip
                          label={tariffTypeMap[site.tariff] || "Unknown"}
                          sx={{
                            backgroundColor: tariffstatusColor(tariffTypeMap[site.tariff] || "Unknown"),
                            color: "#fff", // text color
                            boxShadow: "none"
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: "10px", md: "14px", textAlign: "center" } }}>
                        <Chip
                          label={userTypeMap[site.user_type] || "Unknown"}
                          sx={{
                            backgroundColor: statusColor(userTypeMap[site.user_type] || "Unknown"),
                            color: "#fff", // text color
                            boxShadow: "none"
                          }}
                        />

                      </TableCell>
                      {/* <TableCell sx={{ textAlign: "center" }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            margin: "auto",
                            backgroundColor: plantStatus === 1 ? "success.main" : "error.main",
                          }}
                        />
                      </TableCell> */}
                      <TableCell sx={{ fontSize: { xs: "10px", md: "14px" }, textAlign: "center" }}>{site.access_level}</TableCell>
                      <TableCell sx={{ fontSize: { xs: "10px", md: "14px" }, textAlign: "center" }}></TableCell>

                      {(accessLevel === 3) && (
                        <TableCell align="center" data-action>
                          <Button
                            sx={{ fontSize: { xs: "15px", md: "24px" } }}
                            aria-controls="action-menu"
                            aria-haspopup="true"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              setAnchorEl(e.currentTarget);
                              setSelectedSite(site);
                            }}
                          >
                            <MdMoreVert />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 15, 25, 50]}
                component="div"
                count={filteredSites.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  color: colors.f2,
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: darkMode ? colors.d3 : colors.l4,
                  '& .MuiSelect-icon': {
                    fill: colors.f2,
                  },
                  '& .MuiTablePagination-actions .MuiIconButton-root': {
                    color: colors.f2,

                  },
                }}
              />
            </TableContainer>
          </Paper>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            openEditPage(selectedSite); // Your existing action
            setAnchorEl(null); // Close the menu
          }}
        >
          Modify
        </MenuItem>
        <MenuItem
          onClick={() => {
            setIsDeleteDialogOpen(true); // Your existing action
            setAnchorEl(null); // Close the menu
          }}
        >
          Delete
        </MenuItem>
      </Menu>
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong style={{ color: "#d32f2f" }}>{selectedSite?.name}</strong>?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          {/* Cancel Button */}
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="primary">
            No
          </Button>

          {/* Yes Button (disabled until countdown finishes) */}
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={countdown > 0}
            autoFocus
          >
            {countdown > 0
              ? `Yes, Delete (${countdown}s)`
              : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>



    </Box >
  );
};

export default PlantPage;